import { useEffect, useState } from 'react';
import { toBlob } from 'html-to-image';
import { toast } from 'sonner';
import { API_URL as API } from '@/utils/apiConfig';

// Capture the live preview element as the official PNG (cached per `key`)
// so the shared/saved image is always identical to what the user sees.
export function usePreviewPng(ref, key, exportWidth = 1080) {
  const [blobs, setBlobs] = useState({});
  useEffect(() => {
    if (!key || blobs[key]) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await document.fonts?.ready;
        const el = ref.current;
        if (cancelled || !el || !el.offsetWidth) return;
        const opts = { pixelRatio: exportWidth / el.offsetWidth, skipAutoScale: true, cacheBust: false };
        await toBlob(el, opts); // warm-up pass so fonts/images are embedded
        const blob = await toBlob(el, opts);
        if (!cancelled && blob) setBlobs((prev) => ({ ...prev, [key]: blob }));
      } catch (err) {
        console.warn('[shareCard] preview capture failed:', err);
      }
    }, 60);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return key ? blobs[key] || null : null;
}

// Store the exact card as this share's link-preview image.
export async function uploadShareCard(shareId, blob) {
  if (!shareId || !blob) return false;
  const form = new FormData();
  form.append('file', new File([blob], 'card.png', { type: 'image/png' }), 'card.png');
  try {
    const res = await fetch(`${API}/share/${shareId}/og-image`, { method: 'POST', body: form, credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

export const isDespiaIOS = () => {
  const c = document.body?.classList;
  return !!c && (c.contains('despia-iphone') || c.contains('despia-ipad'));
};

// iOS app shell: upload, then Despia's camera-roll command (needs an HTTPS
// URL, URL-encoded). The OS shows the Photos permission prompt the first time.
export async function saveToIOSPhotos(blob, fileName) {
  try {
    toast.info('Saving to Photos…');
    const form = new FormData();
    form.append('file', new File([blob], fileName, { type: 'image/png' }), fileName);
    const res = await fetch(`${API}/share/upload-image`, { method: 'POST', body: form, credentials: 'include' });
    const data = await res.json();
    if (!data?.url) throw new Error('upload failed');
    const httpsUrl = data.url.replace(/^http:\/\//, 'https://');
    const { default: despia } = await import('despia-native');
    despia(`savethisimage://?url=${encodeURIComponent(httpsUrl)}`);
    toast.success('Saved to Photos');
  } catch {
    toast.error('Could not save the image. Please try again.');
  }
}

// Save a PNG to the device — same mechanism as ShareReflectionModal (proven
// on Android): Despia ignores <a download>, so upload and hand Despia the URL.
export async function saveImageBlob(blob, fileName) {
  if (!blob) return;
  if (document.body?.classList?.contains('despia-android')) {
    toast.info('Saving to your device…');
    try {
      const form = new FormData();
      form.append('file', new File([blob], fileName, { type: 'image/png' }), fileName);
      const res = await fetch(`${API}/share/upload-image`, { method: 'POST', body: form, credentials: 'include' });
      const data = await res.json();
      if (!data?.url) throw new Error('upload failed');
      const { default: despia } = await import('despia-native');
      despia(data.url);
    } catch {
      toast.error('Could not save the image. Please try again.');
    }
    return;
  }
  if (isDespiaIOS()) {
    await saveToIOSPhotos(blob, fileName);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast.success('Image saved');
}
